/**
 * Cognara 4D Linkage — human review for ONE discipline schedule.
 *
 * Same console as the macro review, reading the discipline register instead of
 * the macro WBS: Engineering documents, Procurement MCI lines, F&C (DPR)
 * activities or Pre-commissioning tags. An accepted link is live immediately —
 * the KPI overlays switch from "via macro activity" to the direct link.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { toast } from 'sonner';
import {
  Sparkles, Check, X, RotateCcw, Loader2, Crosshair, Info, Pencil, CheckCheck, Link2,
  ChevronRight, ChevronDown,
} from 'lucide-react';
import { ObjectScopeEditor } from './ObjectScopeEditor';
import { PlanActualDetail } from './DisciplinePlanActual';

const BAND_STYLE = {
  HIGH: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  MEDIUM: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  LOW: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
};
const STATUS_STYLE = {
  PENDING: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
  ACCEPTED: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  REJECTED: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
};
const pctTxt = (v) => (v == null ? '—' : `${Math.round(v * 10) / 10}%`);

export function DisciplineLinkHitl({ projectId, modelId, scheduleId, discipline, label,
                                     viewerRef, canEdit, onClose, onLinksChanged }) {
  const [reg, setReg] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [threshold, setThreshold] = useState([80]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [editorOpen, setEditorOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);
  const toastRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/cognara-4d-discipline/register',
          { params: { schedule_id: scheduleId, model_id: modelId, discipline } }),
        api.get('/cognara-4d-discipline/run-status', { params: { model_id: modelId, discipline } }),
      ]);
      setReg(r.data);
      setRun(s.data.run || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load this register');
    }
    setLoading(false);
  }, [scheduleId, modelId, discipline]);

  useEffect(() => { setSelectedId(null); setDetail(null); load(); }, [load]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (toastRef.current) { toast.dismiss(toastRef.current); toastRef.current = null; }
    viewerRef?.current?.clearHotspot?.();
  }, [viewerRef]);

  const poll = useCallback((toastId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    toastRef.current = toastId;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get('/cognara-4d-discipline/run-status',
          { params: { model_id: modelId, discipline } });
        const rn = data.run;
        if (!rn) return;
        setRun(rn);
        if (rn.status === 'RUNNING') { toast.loading(rn.phase || 'Cognara is analysing…', { id: toastId }); return; }
        clearInterval(pollRef.current); pollRef.current = null;
        setRunning(false);
        toastRef.current = null;
        if (rn.status === 'FAILED') toast.error(rn.error || 'Analysis failed', { id: toastId });
        else toast.success(`Analysis complete · ${rn.itemsMatched ?? 0}/${rn.itemsAnalysed ?? 0} items matched`,
          { id: toastId });
        await load();
      } catch { /* a transient blip must not kill the run */ }
    }, 4000);
  }, [modelId, discipline, load]);

  useEffect(() => {
    if (run?.status === 'RUNNING' && !pollRef.current) {
      setRunning(true);
      poll(toast.loading(run.phase || 'Cognara is analysing…'));
    }
  }, [run, poll]);

  const runAnalysis = async (force) => {
    setRunning(true);
    const t = toast.loading(`Cognara is reading the 3D model and the ${label} register…`);
    try {
      const { data } = await api.post('/cognara-4d-discipline/propose', { force: !!force },
        { params: { project_id: projectId, model_id: modelId, schedule_id: scheduleId, discipline } });
      if (!data.started && data.run?.status === 'COMPLETE') {
        toast.success('Loaded the previous analysis (no AI spend)', { id: t });
        setRunning(false); await load(); return;
      }
      poll(t);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analysis failed', { id: t });
      setRunning(false);
    }
  };

  // Free, deterministic alternative to an AI run: the register already names the
  // model branch and tags it belongs to.
  const syncTags = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/cognara-4d-discipline/sync-from-tags', { replace: false },
        { params: { project_id: projectId, model_id: modelId, schedule_id: scheduleId, discipline } });
      if (data.proposed) toast.success(`${data.proposed} linkage(s) matched from the register's model tags — no AI used`);
      else toast.info('Nothing new to match — every tagged item is already reviewed.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tag matching failed');
    }
    setSyncing(false);
  };

  const paint = useCallback((objs, status, band) => {
    const ids = (objs || []).map(o => o.dbId).filter(v => v != null);
    if (!ids.length) { viewerRef?.current?.clearHotspot?.(); return; }
    const color = status === 'ACCEPTED' ? '#10B981'
      : status === 'REJECTED' ? '#64748B'
      : band === 'HIGH' ? '#2563EB' : band === 'MEDIUM' ? '#F59E0B' : '#EF4444';
    const ok = viewerRef?.current?.applyHotspot?.(ids.map(dbId => ({ dbId, color })));
    if (ok === false) toast.info('Load the 3D model to preview this linkage.');
  }, [viewerRef]);

  const select = useCallback(async (item) => {
    setSelectedId(item.microId);
    setDetail(null);
    if (!item.proposal) { viewerRef?.current?.clearHotspot?.(); return; }
    try {
      const { data } = await api.get(`/cognara-4d-discipline/proposals/${item.proposal.proposalId}`);
      setDetail(data.proposal);
      paint(data.proposal.proposedObjects, data.proposal.status, data.proposal.confidenceBand);
    } catch { toast.error('Could not load that proposal'); }
  }, [paint, viewerRef]);

  const decide = async (decision, dbIds) => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/cognara-4d-discipline/proposals/${detail.proposalId}/decision`,
        dbIds ? { decision, dbIds } : { decision });
      setDetail(data.proposal);
      paint(data.proposal.proposedObjects, data.proposal.status, data.proposal.confidenceBand);
      setReg(prev => {
        if (!prev) return prev;
        const items = prev.items.map(i => (i.microId === data.proposal.microId
          ? { ...i, proposal: { ...i.proposal, status: data.proposal.status,
                                proposedCount: data.proposal.proposedCount,
                                edited: data.proposal.edited },
              linkedObjectCount: data.proposal.status === 'ACCEPTED' ? data.proposal.proposedCount : 0 }
          : i));
        const counts = { PENDING: 0, ACCEPTED: 0, REJECTED: 0, UNPROPOSED: 0 };
        items.forEach(i => { counts[i.proposal?.status || 'UNPROPOSED'] += 1; });
        return { ...prev, counts, items };
      });
      onLinksChanged?.();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save decision'); return false; }
  };

  // Editing the object scope must not change the review verdict.
  const keepVerdict = (st) => (st === 'ACCEPTED' ? 'ACCEPT' : st === 'REJECTED' ? 'REJECT' : 'RESET');

  const bulkAccept = async (all = false) => {    try {
      const { data } = await api.post('/cognara-4d-discipline/proposals/bulk-accept',
        { min_confidence: all ? 0 : threshold[0] / 100 },
        { params: { model_id: modelId, discipline } });
      if (data.accepted) {
        toast.success(`${data.accepted} link(s) accepted · ${data.objectsLinked} objects linked`);
        await load();
        onLinksChanged?.();
      } else {
        toast.info(all ? 'There is no pending proposal left to accept.'
          : `No pending proposal reaches ${threshold[0]}% confidence — lower the threshold.`);
      }
    } catch { toast.error('Bulk accept failed'); }
  };

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = new Map();
    (reg?.items || []).forEach(i => {
      const st = i.proposal?.status || 'UNPROPOSED';
      if (statusFilter !== 'ALL' && st !== statusFilter) return;
      if (q && !(`${i.code} ${i.name}`.toLowerCase().includes(q))) return;
      const key = i.discipline_tag || i.area || 'Unclassified';
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(i);
    });
    return [...out.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [reg, search, statusFilter]);

  const counts = reg?.counts || {};
  const selected = (reg?.items || []).find(i => i.microId === selectedId) || null;

  return (
    <div className="flex flex-col h-full bg-background" data-testid="discipline-hitl">
      <div className="flex items-center gap-2 px-3 h-10 border-b shrink-0 bg-gradient-to-r from-sky-500/10 via-transparent to-transparent">
        <Sparkles className="w-4 h-4 text-sky-500" />
        <span className="text-sm font-semibold">Cognara 4D Linkage — {label} human review</span>
        {run?.status === 'COMPLETE' && (
          <span className="text-[11px] text-muted-foreground" data-testid="discipline-run-summary">
            Last AI run: {run.itemsMatched ?? 0}/{run.itemsAnalysed ?? 0} items matched · {run.branchesAnalysed ?? 0} branches read
          </span>
        )}
        {run?.status === 'FAILED' && (
          <span className="text-[11px] text-rose-500" data-testid="discipline-run-error">
            {run.error || 'The last analysis failed.'}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {['PENDING', 'ACCEPTED', 'REJECTED'].map(s => (counts[s] ? (
            <Badge key={s} variant="outline" className={`text-[10px] h-5 ${STATUS_STYLE[s]}`}
                   data-testid={`discipline-count-${s.toLowerCase()}`}>
              {s.charAt(0) + s.slice(1).toLowerCase()} {counts[s]}
            </Badge>
          ) : null))}
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={syncing || !canEdit}
                  onClick={syncTags} title="Match linkages from the register's own model branch and tags"
                  data-testid="discipline-sync-tags-btn">
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
            Match from tags
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={running || !canEdit}
                  onClick={() => runAnalysis(!!run)} data-testid="discipline-run-btn">
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {run ? 'Re-run analysis' : 'Run analysis'}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClose}
                  data-testid="discipline-close-btn">Close</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 h-9 border-b shrink-0 text-xs">
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Filter ${label}…`}
               className="h-7 text-xs max-w-[220px]" data-testid="discipline-search" />
        <div className="flex items-center gap-0.5">
          {['ALL', 'PENDING', 'ACCEPTED', 'REJECTED', 'UNPROPOSED'].map(s => (
            <Button key={s} size="sm" variant={statusFilter === s ? 'secondary' : 'ghost'}
                    className="h-6 px-2 text-[10px]" onClick={() => setStatusFilter(s)}
                    data-testid={`discipline-filter-${s.toLowerCase()}`}>
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">Auto-accept ≥ {threshold[0]}%</span>
          <Slider value={threshold} onValueChange={setThreshold} min={50} max={100} step={5}
                  className="w-28" data-testid="discipline-threshold" />
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" disabled={!counts.PENDING || !canEdit}
                  onClick={() => bulkAccept(false)} data-testid="discipline-bulk-accept">
            Accept above threshold
          </Button>
          <Button size="sm" variant="outline"
                  className="h-6 px-2 text-[10px] gap-1 border-emerald-500/50 text-emerald-600"
                  disabled={!counts.PENDING || !canEdit} onClick={() => bulkAccept(true)}
                  data-testid="discipline-accept-all">
            <CheckCheck className="w-3 h-3" /> Accept all ({counts.PENDING || 0})
          </Button>
        </div>
        <span className="ml-auto text-[10px] text-muted-foreground whitespace-nowrap"
              data-testid="discipline-live-hint">
          Accepted links go live in the KPI overlays immediately
        </span>
      </div>

      {run?.status === 'RUNNING' && run.phase && (
        <p className="px-3 py-1 text-[10px] text-muted-foreground border-b" data-testid="discipline-phase">
          {run.phase}
        </p>
      )}

      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
        <ResizablePanel defaultSize={42} minSize={22} className="flex flex-col min-h-0">
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
            ) : !(reg?.items || []).length ? (
              <div className="p-8 text-center text-xs text-muted-foreground" data-testid="discipline-empty">
                No {label} items are registered on this project yet.
              </div>
            ) : groups.map(([group, rows]) => (
              <div key={group} data-testid={`discipline-group-${group}`}>
                <button className="w-full flex items-center gap-1.5 px-2 py-1 bg-muted/40 hover:bg-muted/60 text-left"
                        onClick={() => setCollapsed(prev => {
                          const n = new Set(prev); n.has(group) ? n.delete(group) : n.add(group); return n;
                        })}>
                  {collapsed.has(group) ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  <span className="text-[11px] font-medium">{group}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground">{rows.length} items</span>
                </button>
                {!collapsed.has(group) && rows.map(i => (
                  <button key={i.microId} onClick={() => select(i)}
                          className={`w-full text-left flex items-center gap-2 px-3 py-1.5 border-b border-border/50 transition-colors ${
                            selectedId === i.microId ? 'bg-sky-500/10' : 'hover:bg-muted/40'}`}
                          data-testid={`discipline-item-${i.microId}`}>
                    <span className="text-[10px] font-mono text-sky-300 w-28 shrink-0 truncate">{i.code}</span>
                    <span className="text-xs truncate flex-1 min-w-0" title={i.name}>{i.name}</span>
                    {i.proposal?.edited && <Pencil className="w-3 h-3 text-muted-foreground shrink-0" />}
                    <Badge variant="outline" className="text-[9px] h-4 px-1 shrink-0">{pctTxt(i.progressPct)}</Badge>
                    {i.proposal ? (
                      <>
                        <Badge variant="outline"
                               className={`text-[9px] h-4 px-1 shrink-0 ${BAND_STYLE[i.proposal.confidenceBand] || ''}`}>
                          {Math.round((i.proposal.confidence || 0) * 100)}%
                        </Badge>
                        <Badge variant="outline"
                               className={`text-[9px] h-4 px-1 shrink-0 ${STATUS_STYLE[i.proposal.status] || ''}`}>
                          {i.proposal.status}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground w-12 text-right shrink-0">
                          {i.proposal.proposedCount} obj
                        </span>
                      </>
                    ) : (
                      <span className="text-[9px] text-muted-foreground shrink-0">no proposal</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </ScrollArea>
        </ResizablePanel>

        <ResizableHandle withHandle data-testid="discipline-split-handle" />

        <ResizablePanel defaultSize={58} minSize={25} className="flex flex-col min-h-0">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground gap-2">
              <Crosshair className="w-4 h-4" />
              Select a {label} item to preview its proposed 3D linkage
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b shrink-0 flex items-start gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" data-testid="discipline-detail-name">{selected.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">
                    {selected.code} · {selected.status} · progress {pctTxt(selected.progressPct)}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1" disabled={!detail}
                          onClick={() => paint(detail?.proposedObjects, detail?.status, detail?.confidenceBand)}
                          data-testid="discipline-highlight-btn">
                    <Crosshair className="w-3 h-3" /> Highlight
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1"
                          disabled={!detail || !canEdit} onClick={() => setEditorOpen(true)}
                          data-testid="discipline-edit-objects-btn">
                    <Pencil className="w-3 h-3" /> Edit objects
                  </Button>
                  <Button size="sm" variant="outline"
                          className="h-7 px-2 text-[10px] gap-1 text-emerald-600 border-emerald-500/40"
                          disabled={!detail || !canEdit || !detail?.proposedCount
                                    || detail?.status === 'ACCEPTED'}
                          onClick={() => decide('ACCEPT')} data-testid="discipline-accept-btn">
                    <Check className="w-3 h-3" /> Accept
                  </Button>
                  <Button size="sm" variant="outline"
                          className="h-7 px-2 text-[10px] gap-1 text-rose-600 border-rose-500/40"
                          disabled={!detail || !canEdit} onClick={() => decide('REJECT')}
                          data-testid="discipline-reject-btn">
                    <X className="w-3 h-3" /> Reject
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] gap-1"
                          disabled={!detail || !canEdit} onClick={() => decide('RESET')}
                          data-testid="discipline-reset-btn">
                    <RotateCcw className="w-3 h-3" /> Reset
                  </Button>
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-3 space-y-3">
                  <div className="rounded-md border p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
                      {selected.measure === 'QUANTITY' ? 'DPR plan vs actual' : 'Stage gates — plan vs actual'}
                    </div>
                    <PlanActualDetail measure={selected.measure} pva={selected.planVsActual}
                                      gates={selected.stageGates} />
                  </div>

                  {!detail ? (
                    <div className="rounded-md border p-3 text-xs text-muted-foreground"
                         data-testid="discipline-no-proposal">
                      Cognara has not proposed a 3D linkage for this item yet. Run the analysis, or build the
                      object scope by hand once a proposal exists.
                    </div>
                  ) : (
                    <>
                      <div className="rounded-md border p-2.5 bg-muted/30">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-sky-500" /> Cognara reasoning
                        </div>
                        <p className="text-xs leading-relaxed" data-testid="discipline-rationale">
                          {detail.rationale || '—'}
                        </p>
                        {!!(detail.evidence || []).length && (
                          <ul className="mt-2 space-y-0.5">
                            {detail.evidence.map((e, i) => (
                              <li key={i} className="text-[11px] text-muted-foreground flex gap-1.5">
                                <Info className="w-3 h-3 mt-0.5 shrink-0" /><span>{e}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="rounded-md border p-2.5">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Selection rule</div>
                        <div className="flex flex-wrap gap-1 text-[10px]">
                          {(detail.rule?.branchPaths || []).map((b, i) => (
                            <Badge key={i} variant="outline" className="font-mono text-[10px] h-5">{b}</Badge>
                          ))}
                          {(detail.rule?.nameContains || []).map((n, i) => (
                            <Badge key={`n${i}`} variant="secondary" className="text-[10px] h-5">name ~ {n}</Badge>
                          ))}
                          {detail.rule?.propertyKey && (
                            <Badge variant="secondary" className="text-[10px] h-5">
                              {detail.rule.propertyKey} = {(detail.rule.propertyValues || []).join(' | ') || 'any'}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="rounded-md border">
                        <div className="px-2.5 py-1.5 border-b text-[10px] uppercase tracking-wide text-muted-foreground flex items-center">
                          Proposed objects
                          <span className="ml-auto normal-case" data-testid="discipline-object-count">
                            {detail.proposedCount} object(s)
                          </span>
                        </div>
                        {!detail.proposedCount ? (
                          <div className="p-3 text-xs text-muted-foreground">
                            The rule matched nothing in this model — edit the object scope by hand to link it.
                          </div>
                        ) : (
                          <div className="divide-y max-h-[320px] overflow-auto">
                            {(detail.proposedObjects || []).map(o => (
                              <div key={o.dbId} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-muted/40"
                                   data-testid={`discipline-object-${o.dbId}`}>
                                <span className="text-[10px] font-mono text-muted-foreground w-14 shrink-0">#{o.dbId}</span>
                                <span className="text-xs truncate">{o.objectName || '(unnamed)'}</span>
                                <span className="text-[10px] text-muted-foreground truncate ml-auto max-w-[45%]">{o.objectPath}</span>
                                <Button size="icon" variant="ghost" className="h-5 w-5 shrink-0"
                                        onClick={() => viewerRef?.current?.selectAndZoom?.(o.dbId)} title="Zoom to object">
                                  <Crosshair className="w-3 h-3" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-5 w-5 shrink-0 text-rose-600"
                                        disabled={!canEdit} title="Drop from this linkage"
                                        onClick={() => decide(keepVerdict(detail.status),
                                          (detail.proposedObjects || []).map(x => x.dbId).filter(d => d !== o.dbId))}
                                        data-testid={`discipline-drop-${o.dbId}`}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </ScrollArea>
            </>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>

      {detail && (
        <ObjectScopeEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          projectId={projectId}
          modelId={modelId}
          proposal={detail}
          viewerRef={viewerRef}
          onSave={async (dbIds) => {
            const ok = await decide(keepVerdict(detail.status), dbIds);
            if (ok) toast.success(`Linkage updated · ${dbIds.length} object(s)`);
          }}
        />
      )}
    </div>
  );
}

export default DisciplineLinkHitl;
